import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { HiOutlineDownload, HiOutlineChevronDown, HiOutlineRefresh } from 'react-icons/hi';
import { Button } from '../UI';
import { downloadRows } from '../../utils/exportData';

// Shared filter + export controls for the SuperAdmin list views, so every
// list offers the same date presets, select styling and CSV / Excel export.

// sm:w-auto: the global base styles (styles/_base.scss) give every input/select
// width: 100%, which would put each filter on its own row on desktop.
// Phones keep full width so the stacked filters line up.
export const filterSelectClass =
  'w-full sm:w-auto px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

/**
 * Export dropdown (CSV / Excel).
 * `getRows` may be async — pages use it to fetch every row matching the
 * current filters (see fetchAllPages) rather than only the visible page.
 */
export const ExportMenu = ({ getRows, columns, fileName, label = 'Export', disabled = false, size = 'sm' }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const run = async (format) => {
    setOpen(false);
    setBusy(true);
    try {
      const rows = await getRows();
      if (!rows || rows.length === 0) {
        toast.error('No data to export for the current filters');
        return;
      }
      const cols = typeof columns === 'function' ? columns() : columns;
      downloadRows(rows, cols, fileName, format);
      toast.success(`Exported ${rows.length} row${rows.length === 1 ? '' : 's'}`);
    } catch (err) {
      console.error('Export error:', err);
      toast.error(err?.response?.data?.message || 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    // ml-auto: in a wrapping filter row, Export sits at the right end of its line
    <div className="relative inline-block ml-auto" ref={ref}>
      <Button
        variant="outline"
        size={size}
        loading={busy}
        disabled={disabled}
        icon={<HiOutlineDownload className="w-4 h-4" />}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="inline-flex items-center gap-1">
          {busy ? 'Exporting…' : label}
          {!busy && <HiOutlineChevronDown className="w-3.5 h-3.5" />}
        </span>
      </Button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <button
            type="button"
            onClick={() => run('csv')}
            className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            CSV (.csv)
          </button>
          <button
            type="button"
            onClick={() => run('xlsx')}
            className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
          >
            Excel (.xlsx)
          </button>
        </div>
      )}
    </div>
  );
};

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const presetRange = (key) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysAgo = (n) => new Date(today.getFullYear(), today.getMonth(), today.getDate() - n);
  switch (key) {
    case 'today': return { from: ymd(today), to: ymd(today) };
    case 'yesterday': return { from: ymd(daysAgo(1)), to: ymd(daysAgo(1)) };
    case '7d': return { from: ymd(daysAgo(6)), to: ymd(today) };
    case '30d': return { from: ymd(daysAgo(29)), to: ymd(today) };
    case '90d': return { from: ymd(daysAgo(89)), to: ymd(today) };
    case 'this_month': return { from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(today) };
    case 'last_month': return {
      from: ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      to: ymd(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
    case 'this_year': return { from: ymd(new Date(today.getFullYear(), 0, 1)), to: ymd(today) };
    default: return { from: '', to: '' };
  }
};

const PRESETS = [
  ['', 'All time'],
  ['today', 'Today'],
  ['yesterday', 'Yesterday'],
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
  ['90d', 'Last 90 days'],
  ['this_month', 'This month'],
  ['last_month', 'Last month'],
  ['this_year', 'This year'],
  ['custom', 'Custom range'],
];

const matchPreset = (from, to) => {
  if (!from && !to) return '';
  const hit = PRESETS.find(([k]) => k && k !== 'custom' && presetRange(k).from === from && presetRange(k).to === to);
  return hit ? hit[0] : 'custom';
};

/**
 * Date range filter: a preset select, plus from/to date inputs for a custom
 * range. Values are local "YYYY-MM-DD" strings; convert with dateRangeParams()
 * before sending to the API.
 */
export const DateRangeFilter = ({ from = '', to = '', onChange, label = 'Created' }) => {
  const [preset, setPreset] = useState(() => matchPreset(from, to));

  // Keep the preset select in sync when the parent resets the range.
  useEffect(() => {
    if (!from && !to) setPreset('');
  }, [from, to]);

  const onPreset = (key) => {
    setPreset(key);
    if (key === 'custom') return;
    onChange(presetRange(key));
  };

  return (
    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
      <select
        value={preset}
        onChange={(e) => onPreset(e.target.value)}
        className={filterSelectClass}
        aria-label={`${label} date range`}
      >
        {PRESETS.map(([k, l]) => (
          <option key={k || 'all'} value={k}>{`${label}: ${l}`}</option>
        ))}
      </select>
      {preset === 'custom' && (
        <>
          <input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => onChange({ from: e.target.value, to })}
            className={filterSelectClass}
            aria-label={`${label} from`}
          />
          <span className="text-xs text-gray-400">to</span>
          <input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => onChange({ from, to: e.target.value })}
            className={filterSelectClass}
            aria-label={`${label} to`}
          />
        </>
      )}
    </div>
  );
};

/** Select styled to match the other list filters. `options` is [[value, label], ...]. */
export const FilterSelect = ({ value, onChange, options, allLabel, className, ...props }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={clsx(filterSelectClass, className)}
    {...props}
  >
    {allLabel !== undefined && <option value="">{allLabel}</option>}
    {options.map(([v, l]) => (
      <option key={v} value={v}>{l}</option>
    ))}
  </select>
);

// ── Report tables ──────────────────────────────────────────────────────────
// Reports load their rows in one response, so their filters run on the client.

export const PRIORITY_OPTIONS = [['urgent', 'Urgent'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']];

export const EMPTY_REPORT_FILTERS = { search: '', priority: '', organization: '' };

// Rows with `slaBreached`: true = breached, false = met, null = no SLA applied
export const SLA_FILTER_OPTIONS = [['met', 'SLA met'], ['breached', 'SLA breached'], ['none', 'No SLA']];
export const matchesSla = (row, sla) => {
  if (!sla) return true;
  if (sla === 'met') return row.slaBreached === false;
  if (sla === 'breached') return row.slaBreached === true;
  return row.slaBreached === null || row.slaBreached === undefined;
};
export const slaLabel = (breached) => (breached === null || breached === undefined ? '' : breached ? 'Breached' : 'Met');

/** Distinct, sorted, non-empty values — for building filter options from report rows. */
export const uniqueSorted = (values) =>
  [...new Set(values.filter((v) => v !== null && v !== undefined && v !== ''))].sort((a, b) =>
    String(a).localeCompare(String(b)));

/**
 * Apply EMPTY_REPORT_FILTERS-shaped filters to report rows.
 * `searchOf(row)` returns the values the search box matches against.
 */
export const filterReportRows = (rows, filters, { searchOf, orgOf = (r) => r.organizationName, priorityOf = (r) => r.priority }) => {
  const q = (filters.search || '').trim().toLowerCase();
  return rows.filter((r) =>
    (!q || searchOf(r).some((v) => v !== null && v !== undefined && String(v).toLowerCase().includes(q)))
    && (!filters.priority || priorityOf(r) === filters.priority)
    && (!filters.organization || orgOf(r) === filters.organization));
};

/**
 * Filter + export bar shown above a report table: search, priority and
 * organization filters (each optional), any extra controls as children,
 * then Reset and Export.
 */
export const ReportFilterBar = ({
  filters,
  onChange,
  searchPlaceholder = 'Search…',
  showPriority = true,
  organizations = [],
  extraActive = false,
  onResetExtra,
  children,
  exportProps,
}) => {
  const active = !!(filters.search || filters.priority || filters.organization) || extraActive;
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-200">
      <input
        type="text"
        value={filters.search}
        onChange={(e) => onChange({ ...filters, search: e.target.value })}
        placeholder={searchPlaceholder}
        className={clsx(filterSelectClass, 'flex-1 min-w-[240px]')}
      />
      {showPriority && (
        <FilterSelect
          value={filters.priority}
          onChange={(v) => onChange({ ...filters, priority: v })}
          allLabel="All Priorities"
          options={PRIORITY_OPTIONS}
        />
      )}
      {organizations.length > 1 && (
        <FilterSelect
          value={filters.organization}
          onChange={(v) => onChange({ ...filters, organization: v })}
          allLabel="All Organizations"
          options={organizations.map((o) => [o, o])}
          className="max-w-[220px]"
        />
      )}
      {children}
      <ResetFiltersButton
        active={active}
        onReset={() => { onChange({ ...filters, ...EMPTY_REPORT_FILTERS }); if (onResetExtra) onResetExtra(); }}
      />
      {exportProps && <ExportMenu {...exportProps} />}
    </div>
  );
};

/** "Reset" button shown only while any filter is active. */
export const ResetFiltersButton = ({ active, onReset }) =>
  active ? (
    <Button variant="ghost" size="sm" icon={<HiOutlineRefresh className="w-4 h-4" />} onClick={onReset}>
      Reset
    </Button>
  ) : null;
